import React, { useEffect, useState, useCallback } from "react";
import { api, fmt } from "@/lib/api";
import { RegisterTable, RegisterToolbar, FilterSelect, FilterInput, StatusTag } from "@/components/RegisterTable";
import { DetailDrawer } from "@/components/DetailDrawer";
import JournalEntryForm from "@/components/JournalEntryForm";

/**
 * Generic JE Register page. Used by both CAJE and BSJE register routes.
 *
 * Props:
 *   - kind: "CAJE" | "BSJE"
 *   - title: display title
 *   - blurb: subtitle
 */
export default function JournalEntryRegister({ kind, title, blurb }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: null, q: null });
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [toast, setToast] = useState(null);
  const [formMode, setFormMode] = useState(null); // null | "create" | "edit"

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2800);
  };

  const load = useCallback(() => {
    setLoading(true);
    api.listJournalEntries({ entry_kind: kind, status: filters.status || undefined, q: filters.q || undefined })
      .then(d => { setRows(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [kind, filters.status, filters.q]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!selectedId) { setDetail(null); return; }
    api.getJournalEntry(selectedId).then(setDetail);
  }, [selectedId]);

  const refreshDetail = useCallback(() => {
    if (selectedId) api.getJournalEntry(selectedId).then(setDetail);
  }, [selectedId]);

  const columns = [
    { key: "entry_number", label: "Number", width: "140px",
      render: r => <span className="mono" style={{ fontWeight: 600 }}>{r.entry_number}</span> },
    { key: "period_label", label: "Period", width: "120px",
      render: r => <span>{r.period_label || "—"}{r.period_status === "locked" && <span className="tag ok" style={{ marginLeft: 6, fontSize: 10 }}>locked</span>}</span> },
    { key: "journal_date", label: "Journal Date", width: "110px",
      render: r => fmt.date(r.journal_date) },
    { key: "description", label: "Description",
      render: r => (
        <div>
          <div>{r.description}</div>
          <div className="muted" style={{ fontSize: 11 }}>
            DR {r.debit_account} → CR {r.credit_account}
            {r.reference && <> · ref {r.reference}</>}
          </div>
        </div>
      )},
    { key: "amount_unsigned", label: "Amount", width: "110px", align: "right",
      render: r => <span className="mono">${fmt.currency(r.amount_unsigned)}</span> },
    { key: "status", label: "Status", width: "110px",
      render: r => <StatusTag status={r.status} testid={`je-status-${r.id}`} /> },
    { key: "erp_journal_number", label: "ERP #", width: "120px",
      render: r => r.erp_journal_number
        ? <span className="mono" style={{ fontSize: 12 }}>{r.erp_journal_number}</span>
        : <span className="muted">—</span> },
  ];

  return (
    <div data-testid={`je-register-${kind.toLowerCase()}`}>
      <header style={{ marginBottom: 24, display: "flex", justifyContent: "space-between", alignItems: "flex-end", gap: 16 }}>
        <div>
          <div className="heading h1">{title}</div>
          <div className="muted" style={{ marginTop: 4, fontSize: 14 }}>{blurb}</div>
        </div>
        <button
          className="btn primary"
          onClick={() => setFormMode("create")}
          data-testid={`${kind.toLowerCase()}-new-btn`}
        >New {kind}</button>
      </header>

      <RegisterToolbar testid={`${kind.toLowerCase()}-toolbar`}>
        <FilterSelect
          label="Status"
          value={filters.status}
          onChange={v => setFilters(f => ({ ...f, status: v }))}
          options={[
            { value: "draft", label: "Draft" },
            { value: "posted", label: "Posted" },
            { value: "voided", label: "Voided" },
            { value: "discarded", label: "Discarded" },
          ]}
          testid={`${kind.toLowerCase()}-filter-status`}
        />
        <FilterInput
          label="Search"
          value={filters.q}
          onChange={v => setFilters(f => ({ ...f, q: v }))}
          placeholder="Entry number, description, ERP #"
          testid={`${kind.toLowerCase()}-filter-search`}
        />
        <div style={{ flex: 1 }} />
        <div className="muted" style={{ fontSize: 12 }} data-testid={`${kind.toLowerCase()}-count`}>
          {loading ? "Loading…" : `${rows.length} ${kind} entr${rows.length === 1 ? "y" : "ies"}`}
        </div>
      </RegisterToolbar>

      <RegisterTable
        columns={columns}
        rows={rows}
        onRowClick={(r) => setSelectedId(r.id)}
        emptyText={loading ? "Loading…" : `No ${kind} entries match these filters.`}
        testid={`${kind.toLowerCase()}-register-table`}
      />

      <DetailDrawer
        open={!!selectedId}
        onClose={() => setSelectedId(null)}
        title={detail?.entry_number || `${kind} detail`}
        subtitle={detail ? `${detail.period_label} · ${detail.account_name}` : ""}
        testid={`${kind.toLowerCase()}-detail`}
      >
        {detail && (
          <JeDetail
            detail={detail}
            kind={kind}
            onEdit={() => setFormMode("edit")}
            onAction={refreshDetail}
            onReload={load}
            showToast={showToast}
          />
        )}
      </DetailDrawer>

      {formMode && (
        <JournalEntryForm
          mode={formMode}
          kind={kind}
          initial={formMode === "edit" ? { entry: detail } : {}}
          onClose={() => setFormMode(null)}
          onSaved={(saved) => {
            setFormMode(null);
            showToast(formMode === "edit"
              ? `Saved changes to ${saved.entry_number}`
              : `Created ${saved.entry_number}`);
            load();
            if (formMode === "edit") refreshDetail();
            else setSelectedId(saved.id);
          }}
        />
      )}

      {toast && (
        <div
          className="toast"
          data-testid={`${kind.toLowerCase()}-toast`}
          style={{
            position: "fixed", bottom: 20, right: 20, zIndex: 2000,
            background: "var(--ink, #111)", color: "var(--surface, #fff)",
            padding: "10px 16px", borderRadius: 6, fontSize: 13,
            boxShadow: "0 6px 24px rgba(0,0,0,0.2)",
          }}
        >{toast}</div>
      )}
    </div>
  );
}

function JeDetail({ detail, kind, onEdit, onAction, onReload, showToast }) {
  const [busy, setBusy] = useState(false);
  const lockedPeriod = detail.period_status === "locked";
  const isPrePost = ["draft", "ready_to_post", "approved"].includes(detail.status);
  const isPosted = detail.status === "posted";
  const isReversed = !!detail.reversed_by_id;

  async function runAction(fn, successMsg) {
    setBusy(true);
    try {
      await fn();
      showToast(successMsg);
      onAction && onAction();
      onReload && onReload();
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message || "Action failed";
      showToast(`Failed: ${msg}`);
    } finally {
      setBusy(false);
    }
  }

  async function doPost() {
    const erp = window.prompt(
      `Confirm posting of ${detail.entry_number} to the ERP?\n\nMonth-End Commander does not post journal entries to the ERP — this control register records the controller's confirmation that the entry has been posted upstream.\n\nEnter the ERP journal number (required):`
    );
    if (!erp || !erp.trim()) return;
    await runAction(
      () => api.postJournalEntry(detail.id, erp.trim()),
      `Recorded as posted (ERP ${erp.trim()})`,
    );
  }

  async function doDiscard() {
    const reason = window.prompt(
      `Discard draft ${detail.entry_number}?\n\nThis is a soft-delete — the entry is preserved for audit but no longer appears in active workflows. Provide a reason:`
    );
    if (!reason || !reason.trim()) return;
    await runAction(
      () => api.discardJournalEntry(detail.id, reason.trim()),
      `Discarded ${detail.entry_number}`,
    );
  }

  async function doClone() {
    setBusy(true);
    try {
      const cloned = await api.cloneJournalEntry(detail.id);
      showToast(`Cloned to ${cloned.entry_number}`);
      onReload && onReload();
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message || "Clone failed";
      showToast(`Failed: ${msg}`);
    } finally {
      setBusy(false);
    }
  }

  async function doReverse() {
    const today = new Date().toISOString().slice(0, 10);
    const date = window.prompt(
      `Reverse ${detail.entry_number}?\n\nMonth-End Commander does not post to the ERP — this creates a DRAFT reversal record (DR/CR swapped) for the controller to confirm against the ERP reversal once it has been posted upstream. The original record stays POSTED and immutable.\n\nReversal journal date (YYYY-MM-DD):`,
      today,
    );
    if (!date || !date.trim()) return;
    const reason = window.prompt(`Reversal reason for ${detail.entry_number}:`);
    if (!reason || !reason.trim()) return;
    await runAction(
      () => api.reverseJournalEntry(detail.id, date.trim(), reason.trim()),
      `Reversal draft created for ${detail.entry_number}`,
    );
  }

  return (
    <div>
      <Section label="Summary">
        <KV k="Kind" v={detail.entry_kind} />
        <KV k="Status" v={<StatusTag status={detail.status} />} />
        <KV k="Period" v={detail.period_label} />
        <KV k="Account" v={detail.account_name} />
        <KV k="Journal Date" v={fmt.date(detail.journal_date)} />
        <KV k="Amount" v={<span className="mono">${fmt.currency(detail.amount_unsigned)}</span>} />
        <KV k="DR" v={detail.debit_account} />
        <KV k="CR" v={detail.credit_account} />
        {detail.reference && <KV k="Reference" v={detail.reference} />}
        {kind === "CAJE" && detail.clearing_type && <KV k="Clearing Type" v={detail.clearing_type} />}
        {detail.erp_journal_number && <KV k="ERP Journal #" v={<span className="mono">{detail.erp_journal_number}</span>} />}
        {detail.posted_at && <KV k="Posted At" v={fmt.dateTime(detail.posted_at)} />}
        {detail.voided_at && <KV k="Voided At" v={fmt.dateTime(detail.voided_at)} />}
        {detail.void_reason && <KV k="Void Reason" v={detail.void_reason} />}
        {detail.discarded_at && <KV k="Discarded At" v={fmt.dateTime(detail.discarded_at)} />}
        {detail.discard_reason && <KV k="Discard Reason" v={detail.discard_reason} />}
        {detail.reverses_id && (
          <KV k="Reverses" v={<span className="mono" style={{ fontSize: 11 }}>{detail.reverses_id}</span>} />
        )}
        {detail.reversed_by_id && (
          <KV k="Reversed By" v={<span className="mono" style={{ fontSize: 11 }}>{detail.reversed_by_id}</span>} />
        )}
      </Section>

      {detail.supporting_notes && (
        <Section label="Supporting Notes">
          <div style={{ fontSize: 13, lineHeight: 1.5 }}>{detail.supporting_notes}</div>
        </Section>
      )}

      <Section label="Actions">
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          {/* DRAFT / READY_TO_POST / APPROVED actions */}
          {isPrePost && !lockedPeriod && (
            <>
              <button
                className="btn btn-sm primary"
                onClick={onEdit}
                disabled={busy}
                data-testid={`${kind.toLowerCase()}-edit-btn`}
              >Edit</button>
              <button
                className="btn btn-sm primary"
                onClick={doPost}
                disabled={busy}
                data-testid={`${kind.toLowerCase()}-post-btn`}
              >Confirm ERP posting</button>
              <button
                className="btn btn-sm"
                onClick={doDiscard}
                disabled={busy}
                data-testid={`${kind.toLowerCase()}-discard-btn`}
              >Discard</button>
            </>
          )}

          {/* POSTED actions */}
          {isPosted && !isReversed && !lockedPeriod && (
            <button
              className="btn btn-sm"
              onClick={doReverse}
              disabled={busy}
              data-testid={`${kind.toLowerCase()}-reverse-btn`}
            >Reverse</button>
          )}

          {/* Clone is always available (any status, any period) */}
          <button
            className="btn btn-sm"
            onClick={doClone}
            disabled={busy}
            data-testid={`${kind.toLowerCase()}-clone-btn`}
          >Clone</button>
        </div>

        {lockedPeriod && (
          <div className="muted" style={{ fontSize: 12, marginTop: 8 }}>
            Period is locked — only Clone is available. Use the source period&apos;s Unlock workflow before further changes.
          </div>
        )}
        {isPosted && isReversed && (
          <div className="muted" style={{ fontSize: 12, marginTop: 8 }}>
            Already reversed. The paired reversal is referenced under &ldquo;Reversed By&rdquo; above.
          </div>
        )}
      </Section>

      <Section label="Audit Trail">
        {(detail.audit_trail || []).length === 0
          ? <div className="muted" style={{ fontSize: 12 }}>No audit entries.</div>
          : (detail.audit_trail || []).map(a => (
            <div key={a.id} style={{
              padding: "8px 0",
              borderBottom: "1px solid var(--line)",
              fontSize: 12,
            }} data-testid={`${kind.toLowerCase()}-audit-row`}>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span className="mono" style={{ fontWeight: 600 }}>{a.action}</span>
                <span className="muted">{fmt.dateTime(a.created_at)}</span>
              </div>
              <div className="muted" style={{ marginTop: 2 }}>{a.details}</div>
              <div className="muted" style={{ fontSize: 10, marginTop: 2 }}>by {a.actor_user_id}</div>
            </div>
          ))}
      </Section>
    </div>
  );
}

function Section({ label, children }) {
  return (
    <div style={{ marginBottom: 22 }}>
      <div className="muted" style={{
        fontSize: 11, textTransform: "uppercase", letterSpacing: "0.06em",
        marginBottom: 8, fontWeight: 600,
      }}>{label}</div>
      {children}
    </div>
  );
}

function KV({ k, v }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "140px 1fr", gap: 8, padding: "4px 0", fontSize: 13 }}>
      <div className="muted">{k}</div>
      <div>{v}</div>
    </div>
  );
}
